import { Button, TextInput } from "@inceptionbg/ui-components";
import {
  Dispatch,
  FC,
  SetStateAction,
  useContext,
  useEffect,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { FarmContext } from "../../../../AppContent";
import { IInputComponentProps, Input } from "../../../../Elements/Input";
import { ModeContext } from "../../../../Pages/RPGPage";
import {
  setIsUserProcessing,
  updateBankAccount,
  updateBankAccountRefNumber,
} from "../../../../repos/FarmRepo";
import { IFarmNew, IBankAccount } from "../../../../types/IFarm";
import { IEvidence } from "../../../../types/IFile";
import { inputPattern } from "../../../../utils/InputPatternValidation";
import { EvidencesComponent } from "../../../Files/EvidencesComponent";
import { EvidencesUploadDialog } from "../../../Files/EvidencesUploadDialog";
import { SubSection } from "../../../SubSection";
import { Form } from "../../../Wrapper/FormWrapper";
import { IApprovalConsents } from "../../../ApprovalConsent/ApprovalConsentCheckboxes";

interface Props {
  farmData?: IFarmNew;
  setFarm?: Dispatch<SetStateAction<IFarmNew>>;
}
export const BankAccount: FC<Props> = ({ farmData, setFarm }) => {
  const [formData, setFormData] = useState<IBankAccount>({});
  const [evidences, setEvidences] = useState<IEvidence[]>([]);
  const [edit, setEdit] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [attachmentsOpen, setAttachmentsOpen] = useState(false);
  const [attachments2Open, setAttachments2Open] = useState(false);
  const [isInApproval, setIsInApproval] = useState(false);
  const [isUserProcessing, setIsUserProcessingState] = useState(false);

  const { t } = useTranslation();
  const { farm, reloadFarm, isFarmInApproval } = useContext(FarmContext);
  const isRenew = useContext(ModeContext) === "RENEW";
  const isUserProcessingFarm = !!farm?.registrationApproval?.userProcessing;
  const serverUserProcessing = !!farm?.bankAccount?.approval?.userProcessing;
  const disableFarmAdministrativeProcedureEdit =
    !!farm?.registrationApproval?.administrativeProcedure &&
    farm?.registrationApproval?.approvalStatus !== "WAITING_COMPLEMENT";

  // const disableEditAccountNumber =
  //   farm?.bankAccount?.accountNumber &&
  //   (farm.bankAccount.accountNumber !== formData.accountNumber ||
  //     formData.accountNumber?.startsWith("840"));

  const isBankAccountNumberChanged =
    farm?.bankAccount?.accountNumber &&
    farm.bankAccount.accountNumber !== formData.accountNumber;

  const disableFormEdit =
    farm?.bankAccount?.approval?.adminProcessing === true ||
    farm?.bankAccount?.approval?.approvalStatus === "DECISION_SEND" ||
    (isInApproval &&
      farm?.bankAccount?.approval?.administrativeProcedure === true &&
      farm?.bankAccount?.approval?.approvalStatus !== "WAITING_COMPLEMENT") ||
    disableFarmAdministrativeProcedureEdit;

  useEffect(() => {
    !setFarm && setFormData(farm?.bankAccount || {});
    setIsInApproval(
      isFarmInApproval ||
        (!farm?.bankAccount?.approved && !!farm?.bankAccount?.approval)
    );
  }, [farm, setFarm, isFarmInApproval]);

  useEffect(() => {
    setIsUserProcessingState(serverUserProcessing);
  }, [serverUserProcessing]);

  useEffect(() => {
    if (!setFarm && isUserProcessing) {
      setEdit(true);
      setIsInApproval(false);
    }
  }, [isUserProcessing, setFarm]);

  useEffect(() => {
    !!setFarm &&
      setFarm((e) => ({
        ...e,
        bankAccount: {
          evidences: e.bankAccount?.evidences,
          ...formData,
        },
      }));
  }, [formData, setFarm]);
  const bankInfo: IInputComponentProps[] = [
    {
      label: t("AccountNumber"),
      labelHelper: t("AccountNumberHelper"),
      value: farm?.bankAccount?.accountNumber,
      input: (
        <TextInput
          value={formData.accountNumber}
          setValue={(e) => setFormData({ ...formData, accountNumber: e })}
          required
          inputProps={{ pattern: inputPattern.bankAccount }}
          autoFocus={!setFarm}
          error={!setFarm && !formData.accountNumber}
        />
      ),
      alert: {
        text: !formData.accountNumber ? t("RequiredFieldError") : undefined,
        type:
          farm?.bankAccount?.accountNumber && !farm?.bankAccount?.approved
            ? "info"
            : "error",
        active:
          !setFarm &&
          (!formData.accountNumber || !farm?.bankAccount?.approved) &&
          !["WAITING_COMPLEMENT", "APPROVED", "REJECTED"].includes(
            formData?.approval?.approvalStatus || ""
          ),
      },
    },
    {
      label: t("BankName"),
      value: farm?.bankAccount?.bank?.name,
      hidden: !!setFarm || !farm?.bankAccount?.bank,
    },
    {
      label: t("ReferenceNumber"),
      value: farm?.bankAccount?.referenceNumber,
      input: (
        <TextInput
          value={formData.referenceNumber}
          setValue={(e) => setFormData({ ...formData, referenceNumber: e })}
          required={!isBankAccountNumberChanged}
        />
      ),
      hidden: !formData.accountNumber?.startsWith("840"),
    },
  ];
  const onFormSubmit = () => {
    if (
      !formData.accountNumber?.startsWith("840") ||
      (formData.accountNumber?.startsWith("840") &&
        farm?.bankAccount?.accountNumber !== formData.accountNumber)
    ) {
      setAttachments2Open(true);
    } else {
      setIsLoading(true);
      updateBankAccountRefNumber({
        uuid: formData.uuid,
        referenceNumber: formData.referenceNumber,
      })
        .then(() => {
          reloadFarm(() => {
            setIsUserProcessingState(false);
            setIsLoading(false);
            setEdit(false);
          });
        })
        .catch(() => setIsLoading(false));
    }
  };
  const onBankAccountUpdate = (approvalConsents?: IApprovalConsents) => {
    if (farm?.uuid) {
      const data = evidences.length ? { ...formData, evidences } : formData;
      setIsLoading(true);
      updateBankAccount(farm.uuid, {
        ...data,
        ...approvalConsents,
        approval:
          data.approval?.approvalStatus === "APPROVED"
            ? undefined
            : data.approval,
      })
        .then(() => {
          reloadFarm(() => {
            setIsUserProcessingState(false);
            setIsLoading(false);
            setEdit(false);
          });
        })
        .catch(() => setIsLoading(false));
    }
  };
  const handleStartEdit = () => {
    if (!isFarmInApproval && farm?.bankAccount?.approval?.uuid) {
      if (isUserProcessing) {
        setIsInApproval(false);
        setEdit(true);
      } else {
        setIsLoading(true);
        setIsUserProcessing(farm.bankAccount.approval.uuid, true)
          .then(() => {
            setIsUserProcessingState(true);
            setIsInApproval(false);
            setEdit(true);
            setIsLoading(false);
          })
          .catch(() => setIsLoading(false));
      }
    } else {
      setEdit(true);
    }
  };

  const approvalUuidForProcessing = isFarmInApproval
    ? farm?.registrationApproval?.uuid
    : farm?.bankAccount?.approval?.uuid;

  return (
    <SubSection
      title={t("BankAccountTitleOrdered")}
      expandable={!setFarm}
      alerts={
        setFarm
          ? [
              {
                text: t("MissingAttachment"),
                type: "error",
                hidden: !!farmData?.bankAccount?.evidences?.length,
              },
            ]
          : [
              {
                text: t("RequiredFieldsMissing"),
                type: "error",
                hidden: !!formData.accountNumber,
              },
              {
                text: `${t("WaitingComplementText")}
                      ${farm?.bankAccount?.approval?.statusComment}`,
                type: "error",
                number: formData.approval?.approvalNumber,
                hidden:
                  formData.approval?.approvalStatus !== "WAITING_COMPLEMENT",
              },
              {
                text: t("AccountNumberNotVerifiedInfo"),
                type: "info",
                number: farm?.bankAccount?.approval?.approvalNumber,
                hidden:
                  !farm?.bankAccount?.accountNumber ||
                  !!farm?.bankAccount?.approved ||
                  farm.registrationStatus === "WAITING_COMPLEMENT" ||
                  ["WAITING_COMPLEMENT", "APPROVED", "REJECTED"].includes(
                    farm.bankAccount?.approval?.approvalStatus || ""
                  ),
              },
              {
                text: t("AdminProcessingRequest"),
                type: "info",
                hidden: !farm?.bankAccount?.approval?.adminProcessing,
              },
            ]
      }
    >
      {setFarm ? (
        <div className="form-container">
          {bankInfo.map(
            (e) => !e.hidden && <Input key={e.label} component={e} edit />
          )}
          <div className="flex justify-right">
            <Button
              label={t("UploadAttachments")}
              primary
              onClick={() => setAttachmentsOpen(true)}
              size="s"
            />
          </div>
        </div>
      ) : (
        <Form
          data={bankInfo}
          edit={edit}
          setEdit={setEdit}
          submitButton={{
            editLabel: t(edit ? "Confirm" : "BankAccountEdit"),
            onSubmit: edit ? onFormSubmit : handleStartEdit,
            callOnSubmitWhenNotEdit: true,
            hidden:
              disableFormEdit || (isFarmInApproval && !isUserProcessingFarm),
          }}
          otherButtons={[
            {
              label: t("Cancel"),
              onClick: () => {
                setFormData(farm?.bankAccount || {});
                setEdit(false);
              },
              outlined: true,
              hidden: !approvalUuidForProcessing || !edit || disableFormEdit,
            },
            {
              label: t("UploadAttachments"),
              onClick: () => setAttachmentsOpen(true),
              primary: true,
              hidden:
                isRenew ||
                formData.approved ||
                disableFormEdit ||
                isInApproval ||
                !edit,
            },
          ]}
          onResetForm={() => setFormData(farm?.bankAccount || {})}
          isLoading={isLoading}
        />
      )}
      {!isRenew && (
        <>
          <EvidencesUploadDialog
            isOpen={attachmentsOpen}
            onClose={() => {
              setAttachmentsOpen(false);
              // reloadFarm(() => {
              //   setIsLoading(false);
              //   setEdit(false);
              // });
            }}
            category="BANK_ACCOUNT"
            sectionEvidences={farmData?.bankAccount?.evidences}
            setFarm={setFarm}
            approvalUuid={farm?.bankAccount?.approval?.uuid}
          />
          <EvidencesComponent
            isOpen={attachments2Open}
            onClose={() => setAttachments2Open(false)}
            category="BANK_ACCOUNT"
            evidences={evidences}
            setEvidences={setEvidences}
            onSubmit={onBankAccountUpdate}
            showApprovalConsents
          />
        </>
      )}
    </SubSection>
  );
};
